/**
 * Maps distinct territory/platform API responses to select options.
 * Endpoint returns `{ id, name }[]` (not paginated).
 * @param {Array|Object} res - API response
 * @returns {Array<{label: string, value: string}>}
 */
export function mapPmxDistinctOptions(res) {
  if (!Array.isArray(res)) return [];
  return res
    .filter(({ name, id }) => name && id != null)
    .map(({ name, id }) => ({ label: name, value: id }));
}

export function findHistoricalRecordByValue(rlHistoricalRecordOptions, value) {
  if (!Array.isArray(rlHistoricalRecordOptions)) return undefined;
  return rlHistoricalRecordOptions.find(
    (r) => r.value === value || r.value === (value === "true"),
  );
}

export function findProductTypeByValue(productTypeOptions, value) {
  if (!Array.isArray(productTypeOptions)) return undefined;
  return productTypeOptions.find((r) => r.value === value);
}

const PMX_MULTI_SELECT_DELIMITER = "|";

// Find an territory option by its value (falls back to label for legacy URL params)
export function findTerritoryByValue(territoryOptions, value) {
  if (!Array.isArray(territoryOptions)) return undefined;
  const match = territoryOptions.find((r) => String(r.value) === String(value));
  if (match) return match;
  return territoryOptions.find((r) => String(r.label) === String(value));
}

// Find a product platform option by its value
export function findProductPlatformByValue(productPlatformOptions, value) {
  if (!Array.isArray(productPlatformOptions)) return undefined;
  return productPlatformOptions.find((r) => String(r.value) === String(value));
}

export function findProductConfigByValue(productConfigOptions, value) {
  if (!Array.isArray(productConfigOptions)) return undefined;
  return productConfigOptions.find((r) => String(r.value) === String(value));
}

export function parseMultiSelectParam(param, options, findFn) {
  if (!param) return [];

  if (param.includes(PMX_MULTI_SELECT_DELIMITER)) {
    return param
      .split(PMX_MULTI_SELECT_DELIMITER)
      .map((v) => v.trim())
      .map((v) => findFn(options, v))
      .filter(Boolean);
  }

  const wholeMatch = findFn(options, param);
  if (wholeMatch) {
    return [wholeMatch];
  }

  if (!param.includes(",")) {
    return [];
  }

  return param
    .split(",")
    .map((v) => v.trim())
    .map((v) => findFn(options, v))
    .filter(Boolean);
}

// Serialize multi-select value to pipe-separated IDs (commas may appear in values)
function serializeMultiSelect(value) {
  if (!value || (Array.isArray(value) && value.length === 0)) return "";
  if (Array.isArray(value)) {
    return value.map((v) => v?.value ?? v).join(PMX_MULTI_SELECT_DELIMITER);
  }
  return value?.value ?? value ?? "";
}

function assignMultiSelectFromParam(filterData, field, param, options, findFn) {
  if (!param || options.length === 0) return;
  const parsed = parseMultiSelectParam(param, options, findFn);
  if (parsed.length > 0) {
    filterData[field] = parsed;
  }
}

/**
 * Build filter object from URL search params
 * @param {Object} params - Parsed URL search params
 * @param {Object} options - Available dropdown options
 * @returns {Object} Filter data object for the form
 */
export function buildFilterFromParams(params, options) {
  const {
    rlHistoricalRecordOptions,
    productTypeOptions,
    territoryOptions,
    productPlatformOptions,
    productConfigOptions,
  } = options;

  const filterData = {};

  // Text fields
  if (params.catalogId) filterData.catalogId = params.catalogId;
  if (params.catalogName) filterData.catalogName = params.catalogName;
  if (params.pmxReleaseId) filterData.pmxReleaseId = params.pmxReleaseId;

  // Single select fields
  if (params.isHistoricalRecord) {
    filterData.isHistoricalRecord = findHistoricalRecordByValue(
      rlHistoricalRecordOptions,
      params.isHistoricalRecord,
    );
  }
  if (params.productType) {
    filterData.productType = findProductTypeByValue(
      productTypeOptions,
      params.productType,
    );
  }

  // Multi-select fields (only if options are loaded)
  assignMultiSelectFromParam(
    filterData,
    "territory",
    params.territory,
    territoryOptions,
    findTerritoryByValue,
  );
  assignMultiSelectFromParam(
    filterData,
    "productPlatform",
    params.productPlatform,
    productPlatformOptions,
    findProductPlatformByValue,
  );
  assignMultiSelectFromParam(
    filterData,
    "productConfig",
    params.productConfig,
    productConfigOptions,
    findProductConfigByValue,
  );

  if (params.pageSize) {
    filterData.pageSize = Number(params.pageSize);
  }

  return filterData;
}

// Clean and normalize the PMX filter object for API requests
export function cleanPMXFilter(
  filter,
  pmxPricesAndDatesRange,
  pmxPricesAndDatesSort,
) {
  const filtered = {};

  // Text fields
  if (filter.catalogId) filtered.catalogId = filter.catalogId;
  if (filter.catalogName) filtered.catalogName = filter.catalogName;
  if (filter.pmxReleaseId) filtered.pmxReleaseId = filter.pmxReleaseId;

  // Single select fields - serialize to value
  if (
    filter.isHistoricalRecord &&
    typeof filter.isHistoricalRecord === "object"
  ) {
    filtered.isHistoricalRecord = filter.isHistoricalRecord.value ?? "";
  } else if (filter.isHistoricalRecord) {
    filtered.isHistoricalRecord = filter.isHistoricalRecord;
  }

  if (filter.productType && typeof filter.productType === "object") {
    filtered.productType = filter.productType.value ?? "";
  } else if (filter.productType) {
    filtered.productType = filter.productType;
  }

  // Multi-select fields
  const territoryVal = serializeMultiSelect(filter.territory);
  if (territoryVal) filtered.territory = territoryVal;

  const productPlatformVal = serializeMultiSelect(filter.productPlatform);
  if (productPlatformVal) filtered.productPlatform = productPlatformVal;

  const productConfigVal = serializeMultiSelect(filter.productConfig);
  if (productConfigVal) filtered.productConfig = productConfigVal;

  // Pagination
  if (pmxPricesAndDatesRange)
    filtered.pmxPricesAndDatesRange = pmxPricesAndDatesRange;
  if (pmxPricesAndDatesSort)
    filtered.pmxPricesAndDatesSort = pmxPricesAndDatesSort;
  if (filter.pageSize) filtered.pageSize = filter.pageSize;

  return filtered;
}
